import { useEffect, useState } from 'react';
import { FORM_ERROR } from 'final-form';
import { mapValues, startCase } from 'lodash';
import moment from 'moment';
import 'jspdf-autotable';
import { initiativeTypes } from '../constants/index.js';

/**
 * Load the value of an environment variable and throw an error if not defined
 *
 * @param {string} varName
 * @return {*}
 */
export const requireEnvVar = (varName) => {
  const envVar = process.env[varName];
  if (!envVar) {
    throw new Error(`${varName} environment variable must be defined!`);
  }
  return envVar;
};

/**
 * Return a promise that resolves after `delay` milliseconds
 *
 * @param {int} delay The time, in milliseconds, to sleep
 * @return {Promise}
 */
export const sleep = (delay) =>
  new Promise((resolve) => setTimeout(resolve, delay));

/**
 * Wrap a promise with a function for canceling it.
 *
 * @param {Promise} promise
 * @return {{promise: Promise<any>, cancel: function}}
 */
export const makeCancelable = (promise) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      (error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    },
  };
};

/**
 * Return a Moment object if `dateString` is a valid date string.
 * Otherwise return the falsey value.
 *
 * @param {string|*} dateString
 * @return {*|Moment}
 */
export const stringToMoment = (dateString) => dateString && moment(dateString);

/**
 * Return a date string for the given Moment object.
 *
 * @param {moment} momentObject
 * @return {string}
 */
export const momentToDateString = (momentObject) =>
  moment(momentObject).format('YYYY-MM-DD');

/**
 * Return true/false.
 * today 05-04-....
 * momentObject 04-04-.... - false
 * momentObject 05-04-.... - true
 * momentObject 06-04-.... - true
 * @param {moment} momentObject
 * @return {boolean}
 */
export const isDateTodayOrAfter = (momentObject) => {
  const startOfToday = moment().startOf('day');
  return momentObject.isSameOrAfter(startOfToday, 'day');
};

/**
 * Return a function that can accept an error returned by our API and returns
 * an error object to be used by Final Form.
 *
 * @see https://github.com/final-form/final-form#onsubmit-values-object-callback-errors-object--void--object--promiseobject--void
 * @param {string} [defaultErrorMessage] The error message to use if one is
 *   not provided by the API
 * @return {function}
 */
export const mapFinalFormErrors =
  (defaultErrorMessage = 'An error occurred.') =>
  (serverError) => {
    const { errors, message } = serverError;
    const formErrors = mapValues(errors, (error, name) => {
      const nonEmptyStrings =
        typeof error === 'string'
          ? error
          : error.filter((str) => str.trim() !== '');

      return `${startCase(name)} ${nonEmptyStrings}`;
    });

    formErrors[FORM_ERROR] = message.replace(/_/g, ' ') || defaultErrorMessage;
    return formErrors;
  };

/**
 * Calculate percentage and return 0 if divisor is 0.
 *
 * @param {number} dividend
 * @param {number} divisor
 * @return {number}
 */
export const calculatePercentage = (dividend, divisor) => {
  if (divisor === 0) {
    return 0;
  }

  return Math.round((dividend / divisor) * 100);
};

export function getOrgSlug() {
  if (typeof window !== 'undefined') {
    const url = window.location.href;
    const urlObj = new URL(url);

    const { pathname } = urlObj;

    const segments = pathname.split('/').filter(Boolean);

    // AppRoutes without organization slug (global routes)
    const globalRoutes = [
      'login',
      'sso',
      'forgot-password',
      'reset-password',
      'accept-invitation',
      'survey-submit-email',
      'thank-you',
      'online-events',
      '_not-found',
    ];

    // If first segment is a global route, there's no org slug
    if (segments?.length > 0 && globalRoutes.includes(segments[0])) {
      return null;
    }

    // If first segment is an initiative type without a preceding org slug,
    // this shouldn't happen - but return null to be safe
    if (segments?.length > 0 && initiativeTypes.includes(segments[0])) {
      return null;
    }

    // Otherwise, first segment should be the org slug
    const slug = segments[0];
    return slug || null;
  }
  return null;
}

// Check window size changes Hook
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
};

export const isBase64Image = (base64String) =>
  /^data:image\/[a-zA-Z+]+;base64,/.test(base64String);

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const flattenedTree = (tree) => {
  const flattenedTree = [];
  const pushItemAndChildren = (item) => {
    flattenedTree.push(item);
    item.children.forEach(pushItemAndChildren);
  };
  tree.forEach(pushItemAndChildren);

  return flattenedTree;
};
